import React from 'react'
import '@babel/polyfill'
import { Switch, Route } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Spring, animated } from 'react-spring/renderprops.cjs'
import Homepage from '/js/views/homepage'
import ContentPageLayout from '/js/views/shared/layouts/contentPageLayout'
import LetterPageLayout from '/js/views/shared/layouts/letterPageLayout'
import AppendixPageLayout from '/js/views/shared/layouts/appendixPageLayout'
import MissingPageLayout from '/js/views/shared/layouts/missingPageLayout'
import { generateThemeClasses } from '/js/util'

import PeopleData from '../../data/people.json'
import DanaherAtAGlanceData from '../../data/danaher-at-a-glance.json'
import EnvironmentData from '../../data/environment.json'
import FromOurCeoData from '../../data/from-our-ceo.json'
import Covid19Data from '../../data/covid-19.json'
import ReportingFrameworkAppendixData from '../../data/reporting-framework-appendix.json'
import FoundationalElementsData from '../../data/foundational-elements.json'
import InnovationData from '../../data/innovation.json'

const TRANSITION_TIMING = 1000

class App extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      location: this.props.location,
      isTransitioning: true
    }

    this.theme = this.determineTheme()

    window.addEventListener('resize', this.resetComponentsToView)
  }

  resetComponentsToView = () => {
    this.forceUpdate()
  }

  componentDidMount() {
    setTimeout(() => {
      this.setState({ isTransitioning: false })
    }, TRANSITION_TIMING)
  }

  cleanPathString(path) {
    const string = path.replace(/-/g, '_').replace('/', '')
    return string.length > 0 ? string : 'homepage'
  }

  determineTheme() {
    const { location } = this.state
    const { pages } = this.props.data
    const pageKey = this.cleanPathString(location.pathname)
    const themeKey =
      !!pages[pageKey] && pages[pageKey].theme
        ? pages[pageKey].theme
        : 'danaher-blue'

    return generateThemeClasses(themeKey)
  }

  renderTransitionScreen() {
    const { isTransitioning } = this.state
    const ua = window.navigator.userAgent
    const msie = ua.indexOf('MSIE ')

    // this creates issues with IE so don't do the transition if so
    if (msie >= 0) {
      return null
    }

    return (
      <>
        <Spring
          from={{
            transform: isTransitioning
              ? 'translate3d(-100%,0,0)'
              : 'translate3d(0,0,0)'
          }}
          to={{
            transform: isTransitioning
              ? 'translate3d(0,0,0)'
              : 'translate3d(100%,0,0)'
          }}
        >
          {props => (
            <animated.div
              style={props}
              className={`fixed pointer-events-none inset-0 flex z-top ${this.theme.bgColor}`}
            ></animated.div>
          )}
        </Spring>
      </>
    )
  }

  render() {
    const { location, isTransitioning } = this.state
    const { data } = this.props

    return (
      <>
        {this.renderTransitionScreen()}
        {!isTransitioning && (
          <Switch>
            <Route
              exact
              path="/"
              render={location => (
                <>
                  <Helmet>
                    <title>Home - Danaher 2020 Sustainability Report</title>
                    <meta
                      name="description"
                      content={data.pages.homepage.intro}
                    />
                  </Helmet>

                  <Homepage
                    currentPage={location}
                    shared={data.shared}
                    routes={data.routes}
                    pageData={data.pages.homepage}
                    theme={this.theme}
                  />
                </>
              )}
            />

            <Route
              path="/from-our-ceo"
              render={location => (
                <>
                  <Helmet>
                    <title>
                      {FromOurCeoData.hero.subtitle} - Danaher 2020
                      Sustainability Report
                    </title>
                    <meta
                      name="description"
                      content={FromOurCeoData.intro.text}
                    />
                  </Helmet>
                  <LetterPageLayout
                    currentPage={location}
                    shared={data.shared}
                    routes={data.routes}
                    pageData={FromOurCeoData}
                    theme={this.theme}
                  />
                </>
              )}
            />
            <Route
              path="/covid-19"
              render={location => (
                <>
                  <Helmet>
                    <title>
                      {Covid19Data.hero.subtitle} - Danaher 2020
                      Sustainability Report
                    </title>
                    <meta
                      name="description"
                      content={Covid19Data.intro.text}
                    />
                  </Helmet>
                  <LetterPageLayout
                    currentPage={location}
                    shared={data.shared}
                    routes={data.routes}
                    pageData={Covid19Data}
                    theme={this.theme}
                  />
                </>
              )}
            />
            <Route
              path="/appendix"
              render={location => (
                <>
                  <Helmet>
                    <title>
                      Reporting Framework Appendix - Danaher 2020
                      Sustainability Report
                    </title>
                    <meta
                      name="description"
                      content="Reporting Framework Appendix"
                    />
                  </Helmet>
                  <AppendixPageLayout
                    currentPage={location}
                    shared={data.shared}
                    routes={data.routes}
                    pageData={ReportingFrameworkAppendixData}
                    theme={this.theme}
                  />
                </>
              )}
            />
            <Route
              path="/danaher-at-a-glance"
              render={location => (
                <>
                  <Helmet>
                    <title>
                      {DanaherAtAGlanceData.hero.title} - Danaher 2020
                      Sustainability Report
                    </title>
                    <meta
                      name="description"
                      content={DanaherAtAGlanceData.hero.subtitle}
                    />
                  </Helmet>
                  <ContentPageLayout
                    currentPage={location}
                    shared={data.shared}
                    routes={data.routes}
                    pageData={DanaherAtAGlanceData}
                    theme={this.theme}
                  />
                </>
              )}
            />
            <Route
              path="/people"
              render={location => (
                <>
                  <Helmet>
                    <title>
                      {PeopleData.hero.title} - Danaher 2020
                      Sustainability Report
                    </title>
                    <meta
                      name="description"
                      content={PeopleData.hero.subtitle}
                    />
                  </Helmet>
                  <ContentPageLayout
                    currentPage={location}
                    shared={data.shared}
                    routes={data.routes}
                    pageData={PeopleData}
                    theme={this.theme}
                  />
                </>
              )}
            />

            <Route
              path="/innovation"
              render={location => (
                <>
                  <Helmet>
                    <title>
                      {InnovationData.hero.title} - Danaher 2020
                      Sustainability Report
                    </title>
                    <meta
                      name="description"
                      content={InnovationData.hero.subtitle}
                    />
                  </Helmet>
                  <ContentPageLayout
                    currentPage={location}
                    shared={data.shared}
                    routes={data.routes}
                    pageData={InnovationData}
                    theme={this.theme}
                  />
                </>
              )}
            />

            <Route
              path="/environment"
              render={location => (
                <>
                  <Helmet>
                    <title>
                      {EnvironmentData.hero.title} - Danaher 2020
                      Sustainability Report
                    </title>
                    <meta
                      name="description"
                      content={EnvironmentData.hero.subtitle}
                    />
                  </Helmet>
                  <ContentPageLayout
                    currentPage={location}
                    shared={data.shared}
                    routes={data.routes}
                    pageData={EnvironmentData}
                    theme={this.theme}
                  />
                </>
              )}
            />

            <Route
              path="/foundational-elements"
              render={location => (
                <>
                  <Helmet>
                    <title>
                      {FoundationalElementsData.hero.title} - Danaher
                      2020 Sustainability Report
                    </title>
                    <meta
                      name="description"
                      content={FoundationalElementsData.hero.subtitle}
                    />
                  </Helmet>
                  <ContentPageLayout
                    currentPage={location}
                    shared={data.shared}
                    routes={data.routes}
                    pageData={FoundationalElementsData}
                    theme={this.theme}
                  />
                </>
              )}
            />

            <Route
              render={location => (
                <MissingPageLayout
                  currentPage={location}
                  routes={data.routes}
                  shared={data.shared}
                  theme={this.theme}
                  pageData={data.pages.missing_404}
                />
              )}
            />
          </Switch>
        )}
      </>
    )
  }
}

export default App
